/**
 * Gated HITL bridge for `move_structure` — moves a structure IN PLACE by an
 * exact distance in one patient direction, with the same steps as the
 * Transform tool's move (shift the contour grid, snap it to the image voxel
 * lattice, resample; one 'translate' undo step). Shape and size do not
 * change.
 *
 * Coordinates are DICOM patient (LPS) millimetres: +X towards the patient's
 * left, +Y posterior, +Z superior. The model sends centimetres; they are
 * bounded (±{@link MAX_ABS_MARGIN_CM} cm, a cm/mm mix-up guard), quantized
 * to the clinical grid and converted at the shared choke-point.
 *
 * Unlike a mouse drag, which silently stops at the scan edge, a move that
 * would not fit inside the image is REFUSED with the largest distance that
 * does fit — a clinical value is never silently clamped.
 *
 * The moved structure is computed ONCE when the card is built, on a copy
 * (the structure itself is untouched): hovering the card shows it, and the
 * card states the new centre and the room left before the scan edge.
 * Apply writes exactly that computed copy through the shared in-place gate
 * ({@link gateInPlaceProposal}), which refuses a structure-set switch, a
 * change of approval, or an edit of the structure since the card was
 * built; Reject frees the copy. Pure/React-free; the engine is injected.
 */
import {
    asRecord,
    cmToMmChokepoint,
    failedPlan,
    gateInPlaceProposal,
    MAX_ABS_MARGIN_CM,
    mutatingPreamble,
    notModifiableReason,
    quantizeCmTracked,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type ComputedGeometry,
    type InPlaceEditDeps,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { InPlaceEditOutcome, MoveStructureInput } from '../../contract';

export type MoveDirection = MoveStructureInput['direction'];

/** A translation in patient (LPS) millimetres. */
export type PatientDeltaMm = readonly [x: number, y: number, z: number];

/** Unit vector per patient direction (DICOM LPS). */
export const DIRECTION_VECTORS: Record<MoveDirection, PatientDeltaMm> = {
    left: [1, 0, 0],
    right: [-1, 0, 0],
    posterior: [0, 1, 0],
    anterior: [0, -1, 0],
    superior: [0, 0, 1],
    inferior: [0, 0, -1],
};

/** Below this (mm) a move would not visibly change anything. */
const FIT_TOLERANCE_MM = 0.01;

/** The moved copy: geometry plus its centre of mass (patient mm). */
export interface MovedGeometry extends ComputedGeometry {
    readonly centreMm: PatientDeltaMm | undefined;
}

export interface MoveDeps extends InPlaceEditDeps, PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    /** Largest distance (mm) the structure can move this way and stay
     *  inside the image; Infinity when it already exceeds the image on
     *  that axis (the engine then allows free movement). */
    maxDistanceMm(roiNumber: number, direction: MoveDirection): number;
    /** The structure's centre of mass now (patient mm). */
    centreMm(roiNumber: number): PatientDeltaMm | undefined;
    readonly geometry: InPlaceEditDeps['geometry'] & {
        /** The Transform tool's move steps, on a COPY of the structure. */
        computeMove(
            roiNumber: number,
            deltaMm: PatientDeltaMm
        ): Promise<MovedGeometry>;
    };
}

/** A patient position in cm (2 dp), for the card. */
export interface PositionCm {
    readonly x: number;
    readonly y: number;
    readonly z: number;
}

export interface MoveProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    readonly direction: MoveDirection;
    readonly distanceCm: number;
    /** Set when the requested distance was rounded to the clinical grid. */
    readonly requestedDistanceCm?: number;
    /** Centre of mass before and after the move (patient cm). */
    readonly currentCentreCm?: PositionCm;
    readonly newCentreCm?: PositionCm;
    /** Room left before the scan edge after the move (cm); undefined when
     *  the structure already exceeds the scan on that axis. */
    readonly roomLeftCm?: number;
    apply(): Promise<InPlaceEditOutcome>;
    reject(): InPlaceEditOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type MovePlan = MutatingPlan<MoveProposal>;

function readInput(input: unknown): MoveStructureInput | undefined {
    const o = asRecord(input);
    const direction = o?.direction;
    if (
        typeof o?.roi !== 'string' ||
        !o.roi ||
        typeof direction !== 'string' ||
        !(direction in DIRECTION_VECTORS) ||
        typeof o.distanceCm !== 'number' ||
        !Number.isFinite(o.distanceCm)
    ) {
        return undefined;
    }
    return {
        roi: o.roi,
        direction: direction as MoveDirection,
        distanceCm: o.distanceCm,
    };
}

export async function planMoveStructure(
    call: MutatingToolCall,
    deps: MoveDeps
): Promise<MovePlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const parsed = readInput(call.input);
    if (!parsed) {
        return failedPlan(call.toolCallId, 'invalid move_structure input');
    }
    if (parsed.distanceCm <= 0 || parsed.distanceCm > MAX_ABS_MARGIN_CM) {
        return failedPlan(
            call.toolCallId,
            `the distance must be more than 0 and at most ${MAX_ABS_MARGIN_CM} cm`
        );
    }
    const { cm: distanceCm, requestedCm } = quantizeCmTracked(
        parsed.distanceCm
    );
    if (!(distanceCm > 0)) {
        return failedPlan(
            call.toolCallId,
            'the distance is too small to move the structure'
        );
    }

    const resolved = resolveOneStructure(
        parsed.roi,
        deps.structures,
        deps.structures.map(toListed),
        'move',
        'move'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3: volumeBeforeCm3 } = resolved.source;
    if (!deps.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiableReason(name));
    }

    const distanceMm: number = cmToMmChokepoint(distanceCm);
    let maxMm: number;
    try {
        maxMm = deps.maxDistanceMm(roiNumber, parsed.direction);
    } catch (err) {
        return failedPlan(call.toolCallId, reasonOf(err));
    }
    if (distanceMm > maxMm + FIT_TOLERANCE_MM) {
        // Rounded DOWN to the 0.1 mm grid, so the stated distance fits.
        const maxCm = Math.floor(maxMm * 10) / 100;
        return failedPlan(
            call.toolCallId,
            maxCm > 0
                ? `moving '${name}' ${distanceCm} cm ${parsed.direction} ` +
                      `would take it outside the scan; at most ${maxCm.toFixed(2)} cm fits`
                : `'${name}' is already at the ${parsed.direction} edge of ` +
                      'the scan and cannot move further that way'
        );
    }

    // Captured BEFORE computing, so an edit racing the computation is caught.
    const target = {
        roiNumber,
        name,
        volumeBeforeCm3,
        plannedVersion: deps.editVersion(roiNumber),
        plannedContextId: deps.currentContextId(),
    };
    const unit = DIRECTION_VECTORS[parsed.direction];
    const deltaMm: PatientDeltaMm = [
        unit[0] * distanceMm,
        unit[1] * distanceMm,
        unit[2] * distanceMm,
    ];

    let moved: MovedGeometry;
    try {
        moved = await deps.geometry.computeMove(roiNumber, deltaMm);
    } catch (err) {
        return failedPlan(call.toolCallId, reasonOf(err));
    }
    if (!(moved.resultVolumeCm3 > 0)) {
        deps.geometry.releaseComputed?.(moved.handle);
        return failedPlan(
            call.toolCallId,
            'the moved structure would be empty'
        );
    }

    const before = deps.centreMm(roiNumber);
    const proposal: MoveProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        direction: parsed.direction,
        distanceCm,
        ...(requestedCm !== undefined
            ? { requestedDistanceCm: requestedCm }
            : {}),
        ...(before ? { currentCentreCm: toCm(before) } : {}),
        ...(moved.centreMm ? { newCentreCm: toCm(moved.centreMm) } : {}),
        ...(Number.isFinite(maxMm)
            ? { roomLeftCm: Math.floor((maxMm - distanceMm) * 10) / 100 }
            : {}),
        ...gateInPlaceProposal(moved, target, deps),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}

/** mm → cm at 2 dp (display precision). */
function toCm(p: PatientDeltaMm): PositionCm {
    const r = (v: number) => Math.round(v * 10) / 100;
    return { x: r(p[0]), y: r(p[1]), z: r(p[2]) };
}
